/**
 * MoveTaskCoordinator — orchestrates the cross-component move-to-list
 * animation. Owns a per-task highlight ref read by TodoTaskItem, and
 * queues concurrent moves of the same task id to avoid UI races.
 */

type MoveApi = {
  setActiveListId: (id: string) => void;
  patchTaskList: (taskId: string, targetListId: string) => Promise<void>;
  getTaskCurrentListId: (taskId: string) => string | null;
  revertTaskList: (taskId: string, previousListId: string) => void;
  showErrorToast: (message: string) => void;
};

type Coordinator = {
  move: (taskId: string, targetListId: string) => Promise<void>;
  highlightTaskIdRef: { current: string | null };
};

export function createMoveTaskCoordinator(
  api: MoveApi,
  highlightRef: { current: string | null },
): Coordinator {
  const inFlight = new Map<string, Promise<void>>();

  const move = async (taskId: string, targetListId: string): Promise<void> => {
    // Queue concurrent moves of the same task.
    const prior = inFlight.get(taskId);
    const run = async () => {
      if (prior) { try { await prior; } catch { /* noop */ } }

      const sourceListId = api.getTaskCurrentListId(taskId);
      if (!sourceListId || sourceListId === targetListId) return;

      // Cue highlight for destination render.
      highlightRef.current = taskId;

      try {
        // PATCH first so the target list's listId-change fetch (triggered
        // below by setActiveListId) returns a payload that already contains
        // the moved task — otherwise the fetch can race the PATCH and the
        // card doesn't appear until the next manual refresh.
        await api.patchTaskList(taskId, targetListId);
        api.setActiveListId(targetListId);
      } catch (error) {
        highlightRef.current = null;
        api.revertTaskList(taskId, sourceListId);
        api.showErrorToast(`Move failed: ${(error as Error).message}`);
        throw error;
      }
    };

    const running = run().finally(() => {
      if (inFlight.get(taskId) === running) inFlight.delete(taskId);
    });
    inFlight.set(taskId, running);
    return running;
  };

  return { move, highlightTaskIdRef: highlightRef };
}
